// Event landing page reached from the QR code on printed event handouts (round-tables etc.).
// Intentionally hidden: not linked from navigation, not in sitemap.xml, and marked noindex.
// Czech only for now (the QR points to /stay-in-touch, so the language can change without reprinting). Other language versions are kept outside the repo until needed.
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowRight, CheckCircle2, FileText, PlayCircle } from 'lucide-react';
import { trackFormSubmission } from '@/utils/analytics';
import useScrollReveal from '@/hooks/useScrollReveal';

const CALENDAR_URL = 'https://calendar.app.google/d1zdx9SNCNmx37gz8';
const MARTIN_PHOTO = '/lovable-uploads/martin-faith.jpg';

const copy = {
  cs: {
    seoTitle: 'Zůstaňme v kontaktu',
    seoDescription: 'Pošleme vám podklady z round-tablu i pozvánku na webinář e-mailem.',
    kicker: 'Děkujeme, že jste byli s námi',
    title: 'Zůstaňme',
    titleAccent: 'v kontaktu',
    perks: ['Dnešní podklady v PDF', 'Pozvánka na webinář'],
    subtitle: 'Nechte nám e-mail a pošleme vám dnešní podklady v PDF i pozvánku na náš připravovaný webinář.',
    name: 'Jméno a příjmení',
    namePlaceholder: 'Jana Nováková',
    email: 'Pracovní e-mail',
    emailPlaceholder: 'jana@firma.cz',
    company: 'Firma',
    companyPlaceholder: 'Název firmy',
    note: 'Zpráva',
    notePlaceholder: 'Otázka nebo cokoli, co nám chcete vzkázat',
    wantsCall: 'Mám zájem o krátký hovor s Martinem',
    marketingConsent: 'Chci dostávat pozvánky na webináře/akce a marketingovou komunikaci od Stratodata. Odhlásit se můžu kdykoli.',
    privacyLink: 'Ochrana osobních údajů',
    submit: 'Pošlete mi podklady',
    submitting: 'Odesílám…',
    errorName: 'Zadejte prosím své jméno',
    errorEmail: 'Zadejte prosím platný e-mail',
    errorSubmit: 'Něco se pokazilo. Zkuste to znovu nebo napište na info@stratodata.eu.',
    successTitle: 'Děkujeme!',
    successText: 'Podklady i pozvánku na webinář vám pošleme e-mailem během několika dní po akci.',
    moderatorLabel: 'Moderátor round-tablu',
    moderatorText: 'Chcete v rozhovoru pokračovat? Vyberte si 30minutový termín přímo v kalendáři.',
    moderatorCta: 'Rezervovat hovor',
    aboutText: "Připraveni nechat vaše data pracovat pro vás?",
    aboutCta: 'Navštívit stratodata.eu',
  },
};


const StayInTouch = () => {
  const [searchParams] = useSearchParams();
  const lang = 'cs';
  const c = copy[lang];
  // Which event the visitor came from, so leads in ClickUp can be told apart
  const eventSource = searchParams.get('utm_campaign') || 'event';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [photoOk, setPhotoOk] = useState(true);
  useScrollReveal();

  const formSchema = z.object({
    name: z.string().min(2, { message: c.errorName }),
    email: z.string().email({ message: c.errorEmail }),
    company: z.string().optional(),
    note: z.string().max(2000).optional(),
    wantsCall: z.boolean().optional(),
    marketingConsent: z.boolean().optional(),
    website: z.string().optional(), // honeypot, hidden from humans
  });
  type StayInTouchData = z.infer<typeof formSchema>;

  const form = useForm<StayInTouchData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', email: '', company: '', note: '', wantsCall: false, marketingConsent: false, website: '' },
  });

  const onSubmit = async (data: StayInTouchData) => {
    // Bots fill the hidden field; pretend success without sending anything
    if (data.website) {
      setIsSuccess(true);
      return;
    }
    setIsSubmitting(true);
    try {
      // Same backend as the main contact form (web-crm-integration → n8n → ClickUp).
      // Event-specific answers travel in `message` so no backend change is needed.
      const yesNo = (v?: boolean) => (v ? 'yes' : 'no');
      const payload = {
        name: data.name,
        email: data.email,
        company: data.company || 'Not specified',
        company_size: 'Not specified',
        message: [
          `[Stay in touch – ${eventSource}]`,
          'Wants event materials + webinar invite by e-mail: yes',
          `Wants a call with Martin: ${yesNo(data.wantsCall)}`,
          `Marketing consent: ${yesNo(data.marketingConsent)}`,
          `Language: ${lang}`,
          ...(data.note?.trim() ? ['', 'Message:', data.note.trim()] : []),
        ].join('\n'),
        phone_number: '',
      };
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setIsSuccess(true);
      form.reset();
      trackFormSubmission('stay_in_touch_form');
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ event: 'contact_form_success', form_name: 'stay_in_touch_form', event_source: eventSource });
    } catch (error) {
      console.error('Error submitting stay-in-touch form:', error);
      toast.error(c.errorSubmit);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO title={c.seoTitle} description={c.seoDescription} canonical="/stay-in-touch" noIndex />

      {/* Slim header: logo only, the page is a focused landing from a QR code */}
      <header className="px-6 py-5 md:py-6 border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <Link to="/" className="inline-block hover:opacity-90 transition-opacity">
            <img src="/lovable-uploads/2bbaa664-89c2-45e5-80a8-fd888d8066c0.png" alt="Stratodata" className="h-8 md:h-10" />
          </Link>
        </div>
      </header>

      <main className="relative flex-1 px-6 py-10 md:py-16 overflow-hidden">
        {/* Soft brand blobs, same treatment as the other landing pages */}
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-[#FFE3CC] rounded-full filter blur-3xl opacity-70 pointer-events-none" />
        <div className="absolute top-1/2 -left-32 w-[360px] h-[360px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-50 pointer-events-none" />

        <div className="relative z-10 max-w-5xl mx-auto grid md:grid-cols-[1.2fr_1fr] gap-10 md:gap-14 items-start">

          {/* Form column */}
          <section className="reveal">
            <p className="inline-block px-3 py-1 mb-4 rounded-full bg-[#FF6B35]/10 text-xs font-semibold uppercase tracking-wide text-[#FF6B35]">
              {c.kicker}
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-[#2A1E47] leading-[1.05] mb-4">
              {c.title} <span className="text-[#FF6B35] whitespace-nowrap">{c.titleAccent}</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-5">{c.subtitle}</p>

            {/* What they get */}
            <ul className="flex flex-wrap gap-3 mb-8">
              {c.perks.map((perk, i) => {
                const Icon = i === 0 ? FileText : PlayCircle;
                return (
                  <li key={perk} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 shadow-sm text-sm font-medium text-[#2A1E47]">
                    <Icon className="w-4 h-4 text-[#FF6B35]" />
                    {perk}
                  </li>
                );
              })}
            </ul>

            <div className="p-6 md:p-8 rounded-2xl bg-[#FFF4E8] border border-gray-200 border-t-4 border-t-[#FF6B35] shadow-lg shadow-[#FF6B35]/10">
              {isSuccess ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-14 h-14 text-[#FF6B35] mx-auto mb-4 animate-fade-in-up" />
                  <h2 className="text-2xl font-bold text-[#2A1E47] mb-2">{c.successTitle}</h2>
                  <p className="text-muted-foreground">{c.successText}</p>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">{c.name}<span className="text-[#FF6B35]" aria-hidden="true"> *</span></FormLabel>
                          <FormControl>
                            <Input placeholder={c.namePlaceholder} autoComplete="name" className="bg-white" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">{c.email}<span className="text-[#FF6B35]" aria-hidden="true"> *</span></FormLabel>
                          <FormControl>
                            <Input type="email" placeholder={c.emailPlaceholder} autoComplete="email" className="bg-white" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">{c.company}</FormLabel>
                          <FormControl>
                            <Input placeholder={c.companyPlaceholder} autoComplete="organization" className="bg-white" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="note"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">{c.note}</FormLabel>
                          <FormControl>
                            <Textarea placeholder={c.notePlaceholder} rows={4} maxLength={2000} className="bg-white resize-y" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Honeypot */}
                    <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...form.register('website')} />

                    {(['wantsCall', 'marketingConsent'] as const).map((name) => (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start gap-3 space-y-0">
                            <FormControl>
                              <Checkbox checked={field.value === true} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer">
                              {c[name]}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-[#FF6B35] text-white text-base font-semibold shadow-md shadow-[#FF6B35]/30 hover:bg-[#FF6B35]/90 hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:hover:translate-y-0"
                    >
                      {isSubmitting ? c.submitting : c.submit}
                      {!isSubmitting && <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />}
                    </button>

                    <p className="text-center text-xs">
                      <Link to="/privacy" className="text-muted-foreground underline hover:text-foreground">{c.privacyLink}</Link>
                    </p>
                  </form>
                </Form>
              )}
            </div>
          </section>

          {/* Context column */}
          <aside className="space-y-6 md:pt-40 reveal">
            <div className="rounded-2xl border border-gray-100 bg-[#FAFAFA] p-6 md:p-8">
              <div className="flex items-center gap-4 mb-4">
                {/* Falls back to initials until the photo file exists */}
                {photoOk ? (
                  <img src={MARTIN_PHOTO} alt="Martin Faith" onError={() => setPhotoOk(false)} className="w-14 h-14 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#2A1E47] text-white flex items-center justify-center text-lg font-bold shrink-0">M</div>
                )}
                <div>
                  <p className="font-bold text-[#2A1E47]">Martin Faith</p>
                  <p className="text-sm text-muted-foreground">CEO, Stratodata</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5">{c.moderatorText}</p>
              <a
                href={CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#2A1E47] text-[#2A1E47] font-medium hover:bg-[#2A1E47] hover:text-white transition-colors"
              >
                {c.moderatorCta}
              </a>
            </div>

            <div className="rounded-2xl border border-gray-100 p-6 md:p-8">
              <h2 className="text-lg font-bold text-[#2A1E47] mb-3">
                Put your <span className="text-[#FF6B35]">data</span> to work
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">{c.aboutText}</p>
              <Link to="/" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#2A1E47] hover:text-[#FF6B35] transition-colors">
                {c.aboutCta}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StayInTouch;
